import { useEffect, useState } from 'react';
import { dmyToIso, isoToDmy } from '../lib/format';
import { CalendarIcon } from './Icons';

type Props = {
  /** Stored value in ISO 'YYYY-MM-DD', or '' when empty. */
  value: string;
  /** Emits ISO 'YYYY-MM-DD', or '' when cleared. */
  onChange: (iso: string) => void;
  readOnly?: boolean;
  required?: boolean;
  id?: string;
};

// dd/mm/aaaa mask. Pure digits auto-format progressively (05032026 → 05/03/2026);
// once the user types a slash it is respected, so single-digit day/month work too
// (5/3/2026). Only digits and '/' are kept.
function maskDmy(raw: string): string {
  const cleaned = raw.replace(/[^\d/]/g, '').slice(0, 10);
  if (!cleaned.includes('/')) {
    const d = cleaned.slice(0, 8);
    let out = d.slice(0, 2);
    if (d.length > 2) out += `/${d.slice(2, 4)}`;
    if (d.length > 4) out += `/${d.slice(4, 8)}`;
    return out;
  }
  const segments = cleaned.split('/');
  const out = [(segments[0] ?? '').slice(0, 2)];
  if (segments.length > 1) out.push((segments[1] ?? '').slice(0, 2));
  if (segments.length > 2) out.push((segments[2] ?? '').slice(0, 4));
  return out.join('/');
}

/**
 * Date field that always shows and accepts dd/mm/aaaa regardless of the
 * browser locale, while storing the ISO 'YYYY-MM-DD' value. The native calendar
 * is preserved via a transparent <input type="date"> overlaid on the icon.
 */
export function DateInput({ value, onChange, readOnly, required, id }: Props) {
  const [text, setText] = useState(() => isoToDmy(value));

  // Reflect external changes (calendar pick, form reset, programmatic updates).
  useEffect(() => {
    setText(isoToDmy(value));
  }, [value]);

  function handleText(raw: string) {
    const masked = maskDmy(raw);
    setText(masked);
    if (masked === '') {
      onChange('');
      return;
    }
    const iso = dmyToIso(masked);
    if (iso !== null) onChange(iso);
  }

  function handleBlur() {
    // Snap back to the stored value if the text is not a complete valid date.
    if (dmyToIso(text) === null && text !== '') setText(isoToDmy(value));
  }

  return (
    <div className="dateinput">
      <input
        id={id}
        className="di-text"
        type="text"
        inputMode="numeric"
        placeholder="dd/mm/aaaa"
        maxLength={10}
        value={text}
        readOnly={readOnly}
        required={required}
        onChange={(e) => handleText(e.target.value)}
        onBlur={handleBlur}
      />
      {!readOnly && (
        <>
          <span className="di-cal">
            <CalendarIcon />
          </span>
          <input
            className="di-native"
            type="date"
            tabIndex={-1}
            aria-hidden="true"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </>
      )}
    </div>
  );
}
